<template>
  <div class="space-y-4">

    <!-- KPI Strip -->
    <div class="grid grid-cols-6 gap-3">
      <div v-for="kpi in kpis" :key="kpi.label"
        :class="[
          'rounded-xl px-4 py-3.5 border',
          kpi.accent
            ? 'bg-gradient-to-br from-primary-600 to-primary-700 border-primary-500 text-white'
            : 'bg-white border-gray-200'
        ]"
      >
        <div :class="['text-[11px] font-semibold', kpi.accent ? 'text-white/85' : 'text-gray-500']">
          {{ kpi.label }}
        </div>
        <div :class="['font-mono text-[25px] font-semibold tracking-tight mt-1.5', kpi.accent ? '' : 'text-gray-900']">
          {{ kpi.value }}
        </div>
        <div :class="['text-[11px] font-semibold mt-1', kpi.accent ? 'text-white/90' : kpi.changeColor || 'text-gray-500']">
          {{ kpi.change }}
        </div>
      </div>
    </div>

    <!-- Engagement Funnel -->
    <div class="bg-white border border-gray-200 rounded-xl overflow-hidden">

      <!-- Funnel header -->
      <div class="px-4 py-3 border-b border-gray-100 flex justify-between items-center flex-wrap gap-4">
        <div>
          <div class="text-sm font-bold text-gray-900">Marketing &amp; sales funnel</div>
          <div class="text-[11.5px] text-gray-500 mt-0.5">Each stage segmented by engagement type — from light MSO services to full acquisition</div>
        </div>
        <div class="flex gap-1.5 flex-wrap">
          <button v-for="chip in oppChips" :key="chip.label"
            :class="[
              'flex items-center gap-1.5 border rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all cursor-pointer',
              chip.active
                ? 'border-primary-300 bg-primary-50 text-primary-700'
                : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
            ]"
            @click="chip.active = !chip.active"
          >
            <span class="w-[7px] h-[7px] rounded-sm inline-block" :style="{ background: chip.color }"></span>
            {{ chip.label }}
            <span class="font-mono text-[10.5px] opacity-65">{{ chip.count }}</span>
          </button>
        </div>
      </div>

      <!-- Funnel summary row -->
      <div class="grid grid-cols-4 border-b border-gray-100">
        <div v-for="(stat, i) in funnelSummary" :key="stat.label"
          :class="['px-4 py-3', i < 3 ? 'border-r border-gray-100' : '']"
        >
          <div class="text-[11px] text-gray-500">{{ stat.label }}</div>
          <div :class="['font-mono text-[19px] font-semibold mt-0.5', stat.color || 'text-gray-900']">
            {{ stat.value }}
          </div>
        </div>
      </div>

      <!-- Funnel bars -->
      <div class="px-6 py-5 flex flex-col gap-2">
        <div v-for="stage in funnelStages" :key="stage.label" class="flex items-center gap-4">
          <!-- Left label -->
          <div class="w-[158px] shrink-0 text-right">
            <div class="flex items-center justify-end gap-2">
              <span class="text-[12.5px] font-semibold text-gray-800">{{ stage.label }}</span>
              <span class="w-[9px] h-[9px] rounded-sm inline-block shrink-0" :style="{ background: stage.color }"></span>
            </div>
            <div class="text-[10px] text-gray-400 mt-0.5">{{ stage.sub }}</div>
            <div class="text-[10.5px] text-gray-500 mt-0.5">{{ stage.pipelineValue }} pipeline</div>
          </div>

          <!-- Bar -->
          <div class="flex-1 flex items-center">
            <div class="flex gap-0.5 transition-all duration-500" :style="{ width: stage.pct + '%', minWidth: '12px', height: '46px' }">
              <div v-for="(seg, si) in stage.segments" :key="si"
                :title="seg.label"
                class="rounded-sm shadow-sm"
                :style="{ width: seg.w + '%', background: seg.color, opacity: seg.opacity }"
              ></div>
            </div>
          </div>

          <!-- Count -->
          <div class="w-[92px] shrink-0 text-right pr-2.5">
            <div class="font-mono text-[17px] font-semibold text-gray-900 leading-none">{{ stage.count }}</div>
            <div class="text-[9.5px] text-gray-400 mt-0.5">practices</div>
          </div>

          <!-- Conversion -->
          <div class="w-[104px] shrink-0">
            <div v-if="stage.conv" class="flex items-center gap-1.5">
              <span class="text-gray-300 text-[13px]">↓</span>
              <div>
                <div :class="['font-mono text-sm font-bold leading-none', stage.convColor]">{{ stage.conv }}</div>
                <div class="text-[9.5px] text-gray-400 mt-0.5">to next</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Two-column bottom -->
    <div class="grid grid-cols-[1.55fr_1fr] gap-4 items-start">

      <!-- Left: Top priority markets -->
      <div class="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
          <div>
            <div class="text-sm font-bold text-gray-900">Where the need is greatest</div>
            <div class="text-[11.5px] text-gray-500 mt-0.5">Underserved markets ranked by opportunity</div>
          </div>
        </div>
        <div class="p-1.5">
          <div v-for="market in topMarkets" :key="market.name"
            class="flex items-center gap-3.5 px-3 py-2.5 rounded-lg"
          >
            <div class="w-[26px] text-center font-mono text-[13px] font-semibold text-gray-400">{{ market.rank }}</div>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-semibold text-gray-900">{{ market.name }}</div>
              <div class="text-[11px] text-gray-500">{{ market.lives }} attributed lives · {{ market.penetration }}% penetrated</div>
            </div>
            <div class="w-[150px]">
              <div class="flex justify-between text-[10px] text-gray-500 mb-0.5">
                <span>Underserved index</span>
                <span class="font-mono font-semibold" :style="{ color: market.color }">{{ market.underserved }}</span>
              </div>
              <div class="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div class="h-full rounded-full" :style="{ width: market.underserved + '%', background: market.color }"></div>
              </div>
            </div>
            <div class="w-[78px] text-right">
              <div class="font-mono text-[13px] font-semibold text-gray-900">{{ market.whitespace }}</div>
              <div class="text-[10px] text-gray-500">whitespace</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right column -->
      <div class="flex flex-col gap-4">

        <!-- Priority actions -->
        <div class="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-100">
            <div class="text-sm font-bold text-gray-900">Priority actions</div>
            <div class="text-[11.5px] text-gray-500 mt-0.5">Ranked by deal impact &amp; timing</div>
          </div>
          <div class="p-1.5">
            <div v-for="action in priorityActions" :key="action.title"
              class="flex gap-2.5 px-3 py-2.5 rounded-lg items-start"
            >
              <div class="w-[38px] shrink-0 text-center">
                <div class="font-mono text-[12px] font-semibold text-gray-900">{{ action.when }}</div>
              </div>
              <div class="flex-1 min-w-0 border-l border-gray-100 pl-3">
                <div class="text-[12.5px] font-semibold text-gray-900 leading-snug">{{ action.title }}</div>
                <div class="text-[11px] text-gray-500 mt-0.5">{{ action.sub }}</div>
                <span class="inline-block mt-1.5 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded"
                  :style="{ color: action.tagColor, background: action.tagBg }"
                >{{ action.tag }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Live signal feed -->
        <div class="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden text-gray-300">
          <div class="px-4 py-3 border-b border-white/[0.08] flex items-center gap-2">
            <span class="w-[7px] h-[7px] rounded-full bg-green-500 inline-block animate-pulse"></span>
            <div class="text-[13.5px] font-bold text-gray-100">Signals from the claims feed</div>
          </div>
          <div class="p-1 pb-2">
            <div v-for="signal in signals" :key="signal.text"
              class="flex gap-2.5 px-2.5 py-2.5"
            >
              <span class="w-2 h-2 rounded-full shrink-0 mt-1.5" :style="{ background: signal.color }"></span>
              <div class="flex-1">
                <div class="text-[12.5px] text-gray-200 leading-snug">{{ signal.text }}</div>
                <div class="text-[10.5px] text-gray-600 mt-0.5">{{ signal.time }} · {{ signal.source }}</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'CommandCenterTab',
  data() {
    return {
      kpis: [
        { label: 'Network lives', value: '58k', change: '▲ 4,200 this quarter', changeColor: 'text-green-700' },
        { label: 'Active platform ARR', value: '$4.2M', change: '▲ 18% YoY', changeColor: 'text-green-700' },
        { label: 'Weighted pipeline', value: '$1.8M', change: '14 open deals', changeColor: 'text-gray-500' },
        { label: 'Active partners', value: '42', change: 'across 6 metros', changeColor: 'text-gray-500' },
        { label: 'Scored prospects', value: '247', change: 'of 831 practices', changeColor: 'text-gray-500' },
        { label: 'Priority markets', value: '8', change: 'high underserved need', accent: true },
      ],

      oppChips: [
        { label: 'MSO', count: 89, color: '#E5622A', active: true },
        { label: 'VBC Enablement', count: 34, color: '#2E9C8E', active: true },
        { label: 'Full Risk', count: 12, color: '#5B6ABF', active: true },
        { label: 'Acquisition', count: 6, color: '#BF5B93', active: true },
      ],

      funnelSummary: [
        { label: 'In funnel', value: '141 practices' },
        { label: 'Weighted pipeline', value: '$1.8M', color: 'text-primary-600' },
        { label: 'Sourced → Won', value: '18%' },
        { label: 'Avg. annual value', value: '$42k' },
      ],

      funnelStages: [
        {
          label: 'Identified', sub: 'Claims-scored, no contact', pipelineValue: '$680k', color: '#D6D1C8',
          count: 247, pct: 100, conv: '38%', convColor: 'text-gray-600',
          segments: [
            { w: 55, color: '#E5622A', opacity: 0.35, label: 'MSO' },
            { w: 25, color: '#2E9C8E', opacity: 0.35, label: 'VBC Enablement' },
            { w: 12, color: '#5B6ABF', opacity: 0.35, label: 'Full Risk' },
            { w: 8, color: '#BF5B93', opacity: 0.35, label: 'Acquisition' },
          ],
        },
        {
          label: 'Engaged', sub: 'Initial outreach made', pipelineValue: '$420k', color: '#C4BDB1',
          count: 94, pct: 76, conv: '45%', convColor: 'text-gray-600',
          segments: [
            { w: 50, color: '#E5622A', opacity: 0.55, label: 'MSO' },
            { w: 28, color: '#2E9C8E', opacity: 0.55, label: 'VBC Enablement' },
            { w: 14, color: '#5B6ABF', opacity: 0.55, label: 'Full Risk' },
            { w: 8, color: '#BF5B93', opacity: 0.55, label: 'Acquisition' },
          ],
        },
        {
          label: 'Evaluating', sub: 'Deep-dive meeting held', pipelineValue: '$310k', color: '#E5622A',
          count: 42, pct: 52, conv: '52%', convColor: 'text-green-700',
          segments: [
            { w: 42, color: '#E5622A', opacity: 0.75, label: 'MSO' },
            { w: 30, color: '#2E9C8E', opacity: 0.75, label: 'VBC Enablement' },
            { w: 18, color: '#5B6ABF', opacity: 0.75, label: 'Full Risk' },
            { w: 10, color: '#BF5B93', opacity: 0.75, label: 'Acquisition' },
          ],
        },
        {
          label: 'Contracting', sub: 'LOI or terms in progress', pipelineValue: '$220k', color: '#D4872A',
          count: 22, pct: 36, conv: '68%', convColor: 'text-green-700',
          segments: [
            { w: 38, color: '#E5622A', opacity: 0.9, label: 'MSO' },
            { w: 32, color: '#2E9C8E', opacity: 0.9, label: 'VBC Enablement' },
            { w: 20, color: '#5B6ABF', opacity: 0.9, label: 'Full Risk' },
            { w: 10, color: '#BF5B93', opacity: 0.9, label: 'Acquisition' },
          ],
        },
        {
          label: 'Won', sub: 'Signed, in onboarding', pipelineValue: '$170k', color: '#0F8A5F',
          count: 15, pct: 24, conv: null, convColor: '',
          segments: [
            { w: 35, color: '#E5622A', opacity: 1, label: 'MSO' },
            { w: 30, color: '#2E9C8E', opacity: 1, label: 'VBC Enablement' },
            { w: 22, color: '#5B6ABF', opacity: 1, label: 'Full Risk' },
            { w: 13, color: '#BF5B93', opacity: 1, label: 'Acquisition' },
          ],
        },
      ],

      topMarkets: [
        { rank: '1', name: 'Rio Grande Valley', lives: '14.2k', penetration: 3, underserved: 96, whitespace: '13.8k', color: '#E5622A' },
        { rank: '2', name: 'West Texas — Permian Basin', lives: '8.1k', penetration: 0, underserved: 92, whitespace: '8.1k', color: '#E5622A' },
        { rank: '3', name: 'East Texas — Tyler / Longview', lives: '11.4k', penetration: 5, underserved: 88, whitespace: '10.8k', color: '#D4872A' },
        { rank: '4', name: 'San Antonio — South Side', lives: '22.3k', penetration: 8, underserved: 84, whitespace: '20.5k', color: '#D4872A' },
        { rank: '5', name: 'Corpus Christi — Coastal Bend', lives: '6.7k', penetration: 2, underserved: 81, whitespace: '6.6k', color: '#D4872A' },
      ],

      priorityActions: [
        { when: 'Today', title: 'Send MSO proposal to Lone Star Primary Care', sub: 'Rio Grande Valley · 340 lives · 3 providers', tag: 'CONTRACTING', tagColor: '#D4872A', tagBg: 'rgba(212,135,42,0.13)' },
        { when: 'Tue', title: 'Follow up with Desert Sage Health on billing pilot', sub: 'West Texas · 180 lives · credentialing discussion', tag: 'EVALUATING', tagColor: '#E5622A', tagBg: 'rgba(229,98,42,0.13)' },
        { when: 'Wed', title: 'Intro call — Guadalupe Medical Associates', sub: 'San Antonio · 520 lives · referred by Memorial Health', tag: 'OUTREACH', tagColor: '#5B6ABF', tagBg: 'rgba(91,106,191,0.13)' },
        { when: 'Thu', title: 'Prepare board deck for Sunrise Community Health', sub: 'Corpus Christi · 890 lives · final approval stage', tag: 'CONTRACTING', tagColor: '#D4872A', tagBg: 'rgba(212,135,42,0.13)' },
        { when: 'Fri', title: 'Site visit — Heritage Family Medicine (onboarding)', sub: 'East Texas · 270 lives · go-live in 3 weeks', tag: 'ONBOARDING', tagColor: '#0F8A5F', tagBg: 'rgba(15,138,95,0.13)' },
      ],

      signals: [
        { text: 'Lone Star Primary Care AWV rate dropped to 18% — 4x below regional avg. Likely no VBC partner.', time: '2h ago', source: 'CCLF Part B', color: '#E5622A' },
        { text: 'New 14-provider group appeared in Laredo market — Heritage Medical Group, 340 attributed lives.', time: '6h ago', source: 'NPPES + Claims', color: '#2E9C8E' },
        { text: 'Desert Sage Health HCC recapture trending up +12% QoQ — engagement taking hold.', time: '1d ago', source: 'Claims analysis', color: '#3FBF86' },
        { text: 'Guadalupe Medical lost 2 providers (NPI deactivated) — potential staffing pressure point.', time: '2d ago', source: 'NPPES delta', color: '#BF5B93' },
      ],
    }
  },
}
</script>
